<template>
	<UButton
		type="submit"
		:to="to"
		:external="external"
		:color="uColor"
		:variant="uVariant"
		:disabled="loading || disabled"
		:class="buttonClasses"
	>
		<span v-if="loading" class="spinner">
			<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
				<path fill="currentColor" d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z"><animateTransform attributeName="transform" dur="0.75s" repeatCount="indefinite" type="rotate" values="0 12 12;360 12 12" /></path>
			</svg>
		</span>
		<span class="button-content" v-else>
			<slot></slot>
		</span>
	</UButton>
</template>

<script lang="ts" setup>
type ButtonProps = {
	loading?: boolean;
	spinnerInvert?: boolean;
	variant?: "outlined" | "ghost" | "floating";
	colorway?: "success" | "pending" | "warning" | "info" | "danger" | "secondary" | "customer-primary";
	paneled?: boolean;
	disabled?: boolean;
	partiallyDisabled?: boolean;

	to?: string;
	external?: boolean;
};

const props = defineProps<ButtonProps>();

// Semantic mapping onto Nuxt UI; the exact pixel look stays pinned by the
// unlayered scoped SCSS below, which beats the layered Tailwind utilities.
const COLORWAY_TO_COLOR = {
	success: "success",
	pending: "warning",
	warning: "warning",
	info: "info",
	danger: "error",
	secondary: "secondary",
	"customer-primary": "primary",
} as const;

const VARIANT_TO_UVARIANT = {
	outlined: "outline",
	ghost: "ghost",
	floating: "soft",
} as const;

const uColor = computed(() => (props.colorway ? COLORWAY_TO_COLOR[props.colorway] : "neutral"));
const uVariant = computed(() => (props.variant ? VARIANT_TO_UVARIANT[props.variant] : "solid"));

const buttonClasses = computed(() => ({
	"ls-button": true,
	"button--outlined": props.variant === "outlined",
	"button--ghost": props.variant === "ghost",
	"button--floating": props.variant === "floating",
	"spinner-inverted": props.spinnerInvert,
	paneled: props.paneled,
	"button--loading": props.loading,
	[props.colorway || ""]: !!props.colorway,
	"partially-disabled": props.partiallyDisabled,
}));
</script>

<style lang="scss" scoped>
button,
a {
	border: 1px solid var(--background-900);
	background-color: var(--background-900);
	color: var(--text-950);
	font-family: var(--default-font-stack);
	font-weight: 400;
	border-radius: 6px;
	padding: 0.5rem 1rem;
	cursor: pointer;
	opacity: 1;
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: var(--ls-input-height);
	min-height: var(--ls-input-height);
	width: auto;
	transition:
		border-color 0.3s cubic-bezier(0.075, 0.82, 0.165, 1),
		opacity 0.5s cubic-bezier(0.075, 0.82, 0.165, 1),
		background-color 0.3s cubic-bezier(0.075, 0.82, 0.165, 1),
		color 0.3s cubic-bezier(0.075, 0.82, 0.165, 1);

	touch-action: manipulation;
	-webkit-tap-highlight-color: transparent;

	&:hover {
		background-color: var(--background-600);
		border-color: var(--background-600);
	}

	&:active {
		background-color: var(--background-800);
	}

	&.partially-disabled {
		opacity: 0.5;

		&:hover {
			opacity: 0.8;
		}
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	&.success {
		border: 1px solid var(--green-500);
		background-color: var(--green-500);
		color: var(--green-950);

		.spinner svg {
			color: var(--green-950);
			fill: var(--green-950);
		}

		&:hover {
			background-color: var(--green-400);
			color: var(--green-950);
		}

		&:active {
			background-color: var(--green-600);
			color: var(--green-950);
		}
	}

	&.secondary {
		border: 1px solid var(--ls-maroon-legible-color);
		background-color: var(--ls-maroon-legible-color);
		color: var(--black-50);

		.spinner svg {
			color: var(--black-50);
			fill: var(--black-50);
		}

		&:hover {
			background-color: var(--ls-maroon-legible-hover-color);
			border-color: var(--ls-maroon-legible-hover-color);
			color: var(--black-50);
		}

		&:active {
			background-color: var(--ls-maroon-legible-color);
			background-color: var(--ls-maroon-legible-color);
			color: var(--black-50);
		}
	}

	&.danger {
		border: 1px solid var(--red-200);
		background-color: var(--red-200);
		color: var(--red-950);

		&:hover {
			background-color: var(--red-300);
			color: var(--red-950);
		}
	}

	&.customer-primary {
		border: 1px solid var(--ls-primary-legible-color);
		background-color: var(--ls-primary-legible-color);
		color: var(--text-950);

		.spinner svg {
			color: var(--text-950);
			fill: var(--text-950);
		}

		&:hover {
			background-color: var(--ls-primary-legible-2-color);
			border-color: var(--ls-primary-legible-2-color);
			color: var(--text-950);
		}
	}

	&.button--outlined,
	&.button--ghost {
		background-color: transparent;
		color: var(--text-200);
		border-color: var(--background-800);

		.spinner svg {
			color: var(--text-200);
			fill: var(--text-200);
		}

		&:hover {
			background-color: var(--background-200);
			color: var(--text-50);
		}
	}

	&.button--ghost {
		border-color: transparent;

		&:hover {
			background-color: var(--background-300);
			color: var(--text-50);
		}
	}

	&.button--floating {
		background-color: var(--background-300);
		border-color: var(--background-300);
		color: var(--text-50);

		&:hover {
			background-color: var(--background-400);
			color: var(--text-950);
		}
	}

	&.open {
		background-color: var(--background-300);
		color: var(--text-100);
		border-color: var(--text-200);

		&:hover {
			background-color: var(--background-400);
		}
	}

	&.spinner-inverted {
		.spinner {
			svg {
				color: var(--text-950);
				fill: var(--text-950);
			}
		}
	}

	&.button--loading {
		height: var(--ls-input-height);
		min-height: var(--ls-input-height);
	}
}

.spinner {
	position: absolute;
	margin-right: 0.5rem;
	transition: opacity 0.3s cubic-bezier(0.075, 0.82, 0.165, 1);
	height: 100%;
	width: auto;
	aspect-ratio: 1/1;
	display: flex;
	align-items: center;
	justify-content: center;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);

	svg {
		width: 65%;
		height: 65%;
		color: var(--text-50);
		fill: var(--text-50);
	}
}

.button-content {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	width: 100%;
	max-width: 400px;
	min-height: 100%;
	color: inherit;
	font-size: 1rem;
	font-weight: inherit;
	transition:
		max-width 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
		opacity 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
		gap 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);
	gap: 0.5rem;

	&.hidden {
		opacity: 0;
		max-width: 0;
		gap: 0;
	}

	:deep() {
		svg {
			width: 20px;
			min-width: 20px;
			height: 20px;
			min-height: 20px;
			aspect-ratio: 1/1;
		}

		* {
			font-weight: inherit;
			color: inherit;
		}
	}
}
</style>
